import { Menu } from '@base-ui/react/menu';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { RiArrowRightSLine, RiLogoutBoxRLine, RiSettings4Line } from '@remixicon/react';
import type { AccessibleTenant, MeResponse } from '@atnt/shared';
import { useLogout } from '@/shared/api/auth';
import { cn } from '@/shared/lib/cn';
import { dp, useMe } from './use-me';

/** Solid initial avatars use the product chip palette (01-design-system §4.9, table avatars 497:21534). */
const AVATAR_COLORS = ['#0275ff', '#1ecd15', '#f48200', '#7059ff', '#df0259', '#35b5ec'];

function hash(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function UserAvatar({ me, className }: { me: MeResponse; className?: string }) {
  const name = me.user.fullName.trim() || me.user.login;
  if (me.user.avatarUrl) {
    return <img src={me.user.avatarUrl} alt="" className={cn('size-32 shrink-0 rounded-full object-cover', className)} />;
  }
  return (
    <span
      aria-hidden
      className={cn('flex size-32 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white', className)}
      style={{ backgroundColor: AVATAR_COLORS[hash(me.user.id) % AVATAR_COLORS.length] }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/** Subtitle under the user's name: role in the current tenant, or the highest role elsewhere. */
export function useRoleLabel(me: MeResponse, tenant?: AccessibleTenant) {
  const { t } = useTranslation('shell');
  const role = me.isPlatformAdmin
    ? 'platform_admin'
    : (tenant?.role ??
      (me.organizations.some((o) => o.isAdmin) ? 'org_admin' : me.tenants.some((x) => x.isAdmin) ? 'tenant_admin' : 'tenant_member'));
  if (role === 'tenant_member' && me.user.position) return me.user.position;
  return t(`user.roles.${role}`);
}

const itemClass =
  'flex h-32 cursor-pointer items-center gap-8 rounded-4 px-8 text-sm text-fg-nav outline-none select-none data-highlighted:bg-hover';

export const menuPopupClass =
  'glass-popover origin-(--transform-origin) rounded-12 p-6 outline-none transition-[opacity,scale] duration-150 ease-standard data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0';

/**
 * Account area (avatar, name, role, "Chiqish"). The design keeps the user menu in an off-canvas header block
 * (497:21047, "Menu / Two Line": avatar 32 · 14/20 White/90 · 12/16 White/60 · arrow-right-s-line); the shell shows it
 * at the bottom of the sidebar (03-modules §1.1 [P]).
 */
export function UserMenu({ tenant, compact = false }: { tenant?: AccessibleTenant; compact?: boolean }) {
  const { t } = useTranslation('shell');
  const me = useMe();
  const role = useRoleLabel(me, tenant);
  const logout = useLogout();

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={t('user.menu')}
        className={cn(
          'flex shrink-0 items-center rounded-4 text-left outline-none hover:bg-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-2 data-popup-open:bg-hover',
          compact ? 'size-48 justify-center' : 'h-36 w-full gap-12 px-8',
        )}
      >
        <UserAvatar me={me} />
        {!compact && (
          <>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm text-fg-nav">{me.user.fullName}</span>
              <span className="truncate text-xs text-fg-secondary">{role}</span>
            </span>
            <RiArrowRightSLine aria-hidden className="size-20 shrink-0 text-icon" />
          </>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side={compact ? 'right' : 'top'} align={compact ? 'end' : 'start'} sideOffset={() => dp(6)} className="z-50 outline-none">
          <Menu.Popup className={cn(menuPopupClass, compact ? 'w-224' : 'w-(--anchor-width)')}>
            <div className="flex items-center gap-12 px-8 pt-6 pb-8">
              <UserAvatar me={me} />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-fg">{me.user.fullName}</span>
                <span className="truncate text-xs text-fg-secondary">
                  {role} · {me.user.login}
                </span>
              </div>
            </div>
            <Menu.Separator className="-mx-6 my-4 h-px bg-line" />
            <Menu.LinkItem render={<Link to="/settings" />} className={itemClass}>
              <RiSettings4Line aria-hidden className="size-20 shrink-0 text-icon" />
              {t('user.settings')}
            </Menu.LinkItem>
            <Menu.Item
              className={cn(itemClass, 'text-danger-tone')}
              onClick={() => logout.mutate(undefined, { onSettled: () => window.location.assign('/login') })}
            >
              <RiLogoutBoxRLine aria-hidden className="size-20 shrink-0" />
              {t('user.logout')}
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
